import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  MultiSelect,
  Stack,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import { createLabOrder, listTestDefinitions } from "@/lib/api/laboratory";

export function LabOrderDialog({
  examId,
  opened,
  onClose,
}: {
  examId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: definitions } = useQuery({
    queryKey: ["lab-test-definitions"],
    queryFn: listTestDefinitions,
    enabled: opened,
  });

  const form = useForm({
    initialValues: { tests: [] as string[], notes: "" },
    validate: {
      tests: (v) => (v.length > 0 ? null : t("validation.required")),
    },
  });

  const mutation = useMutation({
    mutationFn: (vals: { tests: string[]; notes: string }) =>
      createLabOrder(examId, { tests: vals.tests, notes: vals.notes || null }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["lab-orders"] });
      notifications.show({ color: "green", message: t("laboratory.order_created") });
      form.reset();
      onClose();
    },
  });

  const groups = (definitions ?? []).reduce<Record<string, typeof definitions>>(
    (acc, d) => {
      const key = d.panel ?? "other";
      acc[key] = acc[key] ?? [];
      acc[key]!.push(d);
      return acc;
    },
    {},
  );

  const data = Object.entries(groups).map(([panel, items]) => ({
    group: panel.toUpperCase(),
    items: (items ?? []).map((d) => ({
      value: d.code,
      label: `${d.name_uz ?? d.name_ru} (${d.code})`,
    })),
  }));

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) return mutation.error.message;
    return t("auth.network_error");
  })();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={t("laboratory.order_title")}
      size="lg"
    >
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md" radius="lg">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={form.onSubmit((v) => mutation.mutate(v))}>
        <Stack>
          <MultiSelect
            label={t("laboratory.fields.tests")}
            placeholder={t("placeholder.type_to_search")}
            required
            searchable
            data={data}
            {...form.getInputProps("tests")}
          />
          {form.values.tests.length > 0 && (
            <Group gap="xs">
              {form.values.tests.map((c) => (
                <Badge key={c} variant="light" color="blue" radius="sm" size="md">
                  {c}
                </Badge>
              ))}
            </Group>
          )}
          <Textarea
            label={t("laboratory.fields.notes")}
            placeholder={t("placeholder.notes")}
            minRows={2}
            autosize
            {...form.getInputProps("notes")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={mutation.isPending} radius="xl">
              {t("laboratory.order_submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
